/**
 * Pending Approvals section for the Automations page.
 * Shows automation executions awaiting installer approval (system_control actions).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface PendingExecution {
  id: string;
  rule_id: string;
  property_id: string;
  trigger_snapshot: Record<string, unknown>;
  action_type: string;
  action_result: Record<string, unknown> | null;
  triggered_at: string;
  rule_name: string;
  property_name: string;
}

const RISK_TIER_CONFIG: Record<string, { label: string; color: string; confirmType: 'click' | 'dialog' | 'typed' }> = {
  troubleshooting: { label: 'Troubleshooting', color: 'text-wt-green-primary', confirmType: 'click' },
  optimization: { label: 'Optimization', color: 'text-wt-amber-primary', confirmType: 'dialog' },
  setup: { label: 'Setup', color: 'text-wt-red-primary', confirmType: 'typed' },
};

function usePendingApprovals() {
  const { organization } = useApp();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['pending-approvals', orgId],
    queryFn: async (): Promise<PendingExecution[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('automation_executions')
        .select(`
          id, rule_id, property_id, trigger_snapshot, action_type, action_result, triggered_at,
          automation_rules!inner(name, organization_id),
          properties!inner(name)
        `)
        .eq('outcome', 'pending_approval')
        .eq('automation_rules.organization_id', orgId)
        .order('triggered_at', { ascending: false });

      if (error) throw error;
      return (data ?? []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        rule_id: row.rule_id as string,
        property_id: row.property_id as string,
        trigger_snapshot: (row.trigger_snapshot ?? {}) as Record<string, unknown>,
        action_type: row.action_type as string,
        action_result: row.action_result as Record<string, unknown> | null,
        triggered_at: row.triggered_at as string,
        rule_name: ((row.automation_rules as Record<string, unknown>)?.name ?? 'Unknown') as string,
        property_name: ((row.properties as Record<string, unknown>)?.name ?? 'Unknown') as string,
      }));
    },
    enabled: Boolean(orgId),
    refetchInterval: 30000,
  });
}

function useApproveExecution() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ executionId, action }: { executionId: string; action: 'approve' | 'reject' }) => {
      const { data: session } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-automation-execution`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session?.access_token}`,
          },
          body: JSON.stringify({ execution_id: executionId, action }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to process approval');
      }
      return res.json();
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['pending-approvals'] });
      client.invalidateQueries({ queryKey: ['automation-activity'] });
    },
  });
}

function ApprovalCard({ execution }: { execution: PendingExecution }) {
  const approve = useApproveExecution();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState('');
  const [confirmType, setConfirmType] = useState<'click' | 'dialog' | 'typed'>('dialog');

  const riskTier = (execution.action_result?.risk_tier as string) ?? 'optimization';
  const riskConfig = RISK_TIER_CONFIG[riskTier] ?? RISK_TIER_CONFIG.optimization;
  const description = (execution.action_result?.description as string) ?? 'System settings change requested';
  const settingsChanges = (execution.action_result?.settings_changes as Array<{ field: string; value: unknown }>) ?? [];

  const handleApprove = () => {
    if (riskConfig.confirmType === 'click') {
      approve.mutate(
        { executionId: execution.id, action: 'approve' },
        {
          onSuccess: () => toast.success('Approved'),
          onError: (e) => toast.error(e.message),
        }
      );
    } else {
      setConfirmType(riskConfig.confirmType);
      setConfirmOpen(true);
    }
  };

  const handleConfirmApprove = () => {
    if (confirmType === 'typed' && typedConfirm !== 'APPROVE') return;
    approve.mutate(
      { executionId: execution.id, action: 'approve' },
      {
        onSuccess: () => {
          toast.success('Approved');
          setConfirmOpen(false);
          setTypedConfirm('');
        },
        onError: (e) => toast.error(e.message),
      }
    );
  };

  const handleReject = () => {
    approve.mutate(
      { executionId: execution.id, action: 'reject' },
      {
        onSuccess: () => toast.success('Rejected'),
        onError: (e) => toast.error(e.message),
      }
    );
  };

  const triggeredAgo = formatDistanceToNow(new Date(execution.triggered_at), { addSuffix: true });

  return (
    <>
      <div className="rounded-lg border border-wt-amber-primary/30 bg-wt-bg-secondary p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-wt-text-primary">{execution.rule_name}</h4>
              <Badge variant="outline" className={`text-[10px] h-5 ${riskConfig.color}`}>
                {riskConfig.label}
              </Badge>
            </div>
            <p className="text-xs text-wt-text-secondary mt-1">{execution.property_name}</p>
            <p className="text-xs text-wt-text-muted mt-0.5">{description}</p>
            {settingsChanges.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {settingsChanges.map((change, i) => (
                  <span
                    key={i}
                    className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono bg-wt-bg-tertiary border border-wt-border-subtle text-wt-text-secondary"
                  >
                    {change.field} = {String(change.value)}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Clock className="h-3.5 w-3.5 text-wt-text-muted" />
            <span className="text-[11px] text-wt-text-muted">{triggeredAgo}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-wt-border-subtle">
          <Button
            size="sm"
            variant="default"
            className="h-7 text-xs"
            onClick={handleApprove}
            disabled={approve.isPending}
          >
            {approve.isPending ? (
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            ) : (
              <CheckCircle className="mr-1.5 h-3 w-3" />
            )}
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs text-wt-red-primary hover:text-wt-red-primary"
            onClick={handleReject}
            disabled={approve.isPending}
          >
            <XCircle className="mr-1.5 h-3 w-3" />
            Reject
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm approval</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmType === 'typed' ? (
                <>
                  This is a <strong className="text-wt-red-primary">setup-level</strong> change. Type{' '}
                  <code className="font-mono bg-wt-bg-tertiary px-1 rounded">APPROVE</code> to confirm.
                </>
              ) : (
                <>
                  This will apply settings changes to <strong>{execution.property_name}</strong>. Are you sure?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmType === 'typed' && (
            <Input
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder="Type APPROVE"
              className="font-mono"
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmApprove}
              disabled={confirmType === 'typed' && typedConfirm !== 'APPROVE'}
            >
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function PendingApprovals() {
  const { data: pending, isLoading } = usePendingApprovals();

  if (isLoading || !pending?.length) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-wt-amber-primary animate-pulse" />
        <h3 className="text-sm font-semibold text-wt-text-primary">
          Pending Approvals ({pending.length})
        </h3>
      </div>
      <div className="grid gap-3">
        {pending.map((exec) => (
          <ApprovalCard key={exec.id} execution={exec} />
        ))}
      </div>
    </div>
  );
}
